'use client';
import React, { useEffect, useRef, useState } from 'react';
import cn from '@utils/cn';
import useIsInView from '@lib/hooks/useIsInView';
import Testimonial from '@components/Modules/TestimonialsStatsModule/Testimonial';
import { TestimonialsStatsType } from '@lib/sanity/queries/modules/testimonialsStats/types';

type CarouselProps = {
  items: React.ReactElement[];
  timer?: number;
  data: TestimonialsStatsType;
};

export default function Carousel({ data, items, timer }: CarouselProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const isInView = useIsInView(containerRef);
  const [currentIndex, setCurrentIndex] = useState(0);

  // Auto-advance timer
  useEffect(() => {
    if (!isInView || !timer || items.length <= 1) {
      return;
    }

    const timeout = setTimeout(() => {
      setCurrentIndex(increment(items.length));
    }, timer);

    return () => {
      clearTimeout(timeout);
    };
  }, [isInView, items.length, timer, currentIndex]); // Include currentIndex as a dependency

  // Touch event handling
  useEffect(() => {
    const container = containerRef.current;
    let touchStartX: number | undefined;

    if (!container || items.length <= 1) {
      return;
    }

    function onTouchStart(e: TouchEvent) {
      touchStartX = e.touches[0].clientX;
    }

    function onTouchEnd(e: TouchEvent) {
      if (touchStartX === undefined) {
        return;
      }

      const touchEndX = e.changedTouches[0].clientX;
      const amt = touchStartX - touchEndX;
      touchStartX = undefined;

      if (amt > 50) {
        setCurrentIndex(increment(items.length));
      } else if (amt < -50) {
        setCurrentIndex(decrement(items.length));
      }
    }

    container.addEventListener('touchstart', onTouchStart);
    document.addEventListener('touchend', onTouchEnd);

    return () => {
      container.removeEventListener('touchstart', onTouchStart);
      document.removeEventListener('touchend', onTouchEnd);
    };
  }, [items.length]);

  useEffect(() => {
    function updateHeight() {
      const itemsContainer = containerRef.current?.firstChild as HTMLElement;

      if (itemsContainer) {
        const heights = Array.from(itemsContainer.children).map((child) => (child as HTMLElement).offsetHeight);
        const maxHeight = Math.max(...heights);
        itemsContainer.style.height = `${maxHeight}px`;
      }
    }

    updateHeight();
    window.addEventListener('resize', updateHeight);

    return () => {
      window.removeEventListener('resize', updateHeight);
    };
  }, []);

  if (!items.length) {
    return;
  }

  function renderItem(item: React.ReactElement, i: number) {
    return (
      <div
        key={item.key ?? i}
        className={cn('pointer-events-none absolute opacity-0 transition-opacity duration-500', {
          'pointer-events-auto opacity-100': i === currentIndex,
        })}
      >
        <Testimonial
          itemContent={item}
          data={data?.testimonials?.[i]}
          count={items.length}
          index={currentIndex}
          setCurrentIndex={setCurrentIndex}
        />
      </div>
    );
  }

  return (
    <div ref={containerRef} className="flex w-full flex-col items-center gap-6 pb-10 pt-12 lg:gap-8">
      <div className="relative flex h-full w-full justify-center lg:col-span-10 lg:col-start-2">
        {items.map(renderItem)}
      </div>
    </div>
  );
}

function increment(max: number) {
  return (prevIndex: number) => (prevIndex + 1) % max;
}

function decrement(max: number) {
  return (prevIndex: number) => (prevIndex - 1 + max) % max;
}
